import moment from 'moment';
import { sessionHeaders, getSessionToken } from './sessionStorage';

// Try to load local config, fallback to default if not found
let localConfig = { defaultSubdomain: 'demo' };
try {
    localConfig = require('../local.config.json');
} catch (e) {
    // Local config not found, using defaults
}

export const COLLES = [
    'demo',
    'sarri',
    'esquerdats',
    'berga',
    'matossers',
    'pastoretsvila',
    'ganapies',
    'trempats',
    'arreplegats',
    'bergants',
    'falconsbcn',
    'pataquers',
    'santpedor',
    'llunatics',
    'emboirats',
    'marracos',
    'descargolats',
    'sagals',
    'falconsvila',
    'xoriguers',
    'engrescats',
    'gambirots',
    'passerells',
    'laietans',
    'escatllots',
    'mallorca',
    'falconspiera',
    'eire',
    'potesroges',
    'santfeliu',
    'lena',
    'penjats',
    'grillats',
    'santiago',
    'viladecans',
    'castelldefels',
    'nova',
    'andorra',
    'baixmontseny',
    'dracterrassa',
    'torraires',
    'horta',
    'esperxats',
    'synera',
    'falconsvilanova',
]

export const getSubdomain = (defaultValue=localConfig.defaultSubdomain) => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    // Check if the hostname has at least 3 parts (subdomain, domain, and TLD)
    if (parts.length >= 3) {
      // Return the first part as the subdomain
      const subdomain = parts[0].toLowerCase();
      if (COLLES.includes(subdomain)) {
        return subdomain;
      } else {
        return defaultValue;
      }
    } else {
      // If there's no subdomain, return the default value
      return defaultValue;
    }
}

export const COLLA = getSubdomain()

export const getServerHost = (colla) => `${colla}-api.tenimaleta.com`;

export const getServerEndpoint = (colla) => {
  if (process.env.REACT_APP_LOCAL === 'true' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return window.location.origin;
  }
  const host = getServerHost(colla);
  return `https://${host}`;
};

export const postAPI = (endpoint, data, callback=undefined, errorCallback=undefined) => {
  const headers = {
      ...sessionHeaders(),
      "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557",
      "Content-Type": "application/json"
  };

  const serverEndpoint = getServerEndpoint(COLLA);

  fetch(`${serverEndpoint}/api${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
  })
      .then(response => {
          if (!response.ok) {
              return response.json().then(err => {
                  throw Object.assign(new Error(`${endpoint} - ${response.status}`), { status: response.status, ...err });
              }).catch(parseErr => {
                  if (parseErr.status) throw parseErr;
                  // Express answers unknown routes with HTML: keep the status (404) anyway.
                  throw Object.assign(new Error(`${endpoint} - ${response.status}`), { status: response.status });
              });
          }
          return response.json();
      })
      .then(data => {
          if (callback) callback(data);
      })
      .catch(error => {
          console.error('Post API error:', error);
          if (errorCallback) errorCallback(error);
      });
}

export const putAPI = (endpoint, data, callback=undefined) => {
  const headers = {
      ...sessionHeaders(),
      "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557",
      "Content-Type": "application/json"
  };

  const serverEndpoint = getServerEndpoint(COLLA);

  fetch(`${serverEndpoint}/api${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
  })
      .then(response => {
          if (!response.ok) {
              throw new Error(`${endpoint} - ${response.status}`);
          }
          return response.json();
      })
      .then(data => {
          if (callback) callback(data);
      })
      .catch(error => {
          console.error('Put API error:', error);
      })
}

export const deleteAPI = (endpoint, callback=undefined) => {
  const headers = {
      ...sessionHeaders(),
      "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557"
  };

  const serverEndpoint = getServerEndpoint(COLLA);

  fetch(`${serverEndpoint}/api${endpoint}`, {
      method: 'DELETE',
      headers
  })
      .then(response => {
          if (!response.ok) {
              throw new Error(`${endpoint} - ${response.status}`);
          }
          return response.json();
      })
      .then(data => {
          if (callback) callback(data);
      })
      .catch(error => {
          console.error('Delete API error:', error);
      })
}

// Resolves once the server accepted the request, whatever its body (some routes answer plain text).
export const requestAPI = (method, endpoint, data=undefined) => {
  const headers = {
      ...sessionHeaders(),
      "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557",
      ...(data !== undefined ? { "Content-Type": "application/json" } : {})
  };

  const serverEndpoint = getServerEndpoint(COLLA);

  return fetch(`${serverEndpoint}/api${endpoint}`, {
      method,
      headers,
      ...(data !== undefined ? { body: JSON.stringify(data) } : {})
  }).then(response => {
      if (!response.ok) {
          throw Object.assign(new Error(`${endpoint} - ${response.status}`), { status: response.status });
      }
  });
}

export const fetchAPIPromise = (endpoint, withCache=true) => {
  return new Promise((resolve, reject) => {
    const headers = {
      ...sessionHeaders(),
      "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557"
    };

    const serverEndpoint = getServerEndpoint(COLLA);
    const url = `${serverEndpoint}/api${endpoint}`;
    
    // Check if withCache is true and data for the endpoint is in the cache
    if (withCache && !getSessionToken() && 'caches' in window) {
      caches.open('api-cache').then(cache => {
        cache.match(url).then(response => {
          if (response) {
            // If in cache, get the cached data first
            response.json().then(data => resolve(data));
          }
        });
      });
    }

    // Fetch from API and store in cache
    fetch(url, { headers })
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status + ' ' + response.statusText);
        }
        return response.clone().json();
      })
      .then(data => {
        resolve(data);
        if (!headers.Authorization && 'caches' in window) {
          caches.open('api-cache').then(cache => {
            cache.put(url, new Response(JSON.stringify(data)));
          });
        }
      })
      .catch(error => {
        console.error('There has been a problem with your fetch operation:', error);
        reject(error);
      });
  });
}

export const fetchAPI = (endpoint, callback, withCache=true, errorCallback=undefined) => {
  const headers = {
      ...sessionHeaders(),
    "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557"
  };

  const serverEndpoint = getServerEndpoint(COLLA);
  const url = `${serverEndpoint}/api${endpoint}`;
  
  // Check if withCache is true and data for the endpoint is in the cache
  if (withCache && !getSessionToken() && 'caches' in window) {
    caches.open('api-cache').then(cache => {
      cache.match(url).then(response => {
        if (response) {
          // If in cache, get the cached data first
          response.json().then(data => callback(data));
        }
      });
    });
  }

  // Fetch from API and store in cache
  fetchAndCacheData(url, headers, callback, errorCallback);
}

export const fetchAPIquery = (endpoint, callback, withCache=true, saveCache=true, query={}, errorCallback=undefined) => {
  const headers = {
      ...sessionHeaders(),
      "x-api-key": "453dabb4-7645-4626-a1bb-477dff3aa557"
  };

  const serverEndpoint = getServerEndpoint(COLLA);
  const queryString = Object.keys(query).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`).join('&');
  const url = `${serverEndpoint}/api${endpoint}?${queryString}`;

  // Check if withCache is true and data for the endpoint is in the cache
  if (withCache && !getSessionToken() && 'caches' in window) {
    caches.open('api-cache').then(cache => {
      cache.match(url).then(response => {
        if (response) {
          response.json().then(data => callback(data));
        } else {
          // If not in cache, fetch from API and store in cache
          fetchAndCacheData(url, headers, callback, errorCallback);
        }
      });
    });
  } else {
    // If withCache is false, fetch from API without caching
    fetchAndCacheData(url, headers, callback, errorCallback);
  }
}

// Helper function to fetch data from API and optionally cache it
const fetchAndCacheData = (url, headers, callback, errorCallback=undefined) => {
  fetch(url, { headers })
    .then(response => {
      if (!response.ok) {
        throw new Error(response.status + ' ' + response.statusText);
      }
      return response.clone().json();
    })
    .then(data => {
      callback(data);
      if (!headers.Authorization && 'caches' in window) {
        caches.open('api-cache').then(cache => {
          cache.put(url, new Response(JSON.stringify(data)));
        });
      }
    })
    .catch(error => {
      console.error('There has been a problem with your fetch operation:', error);
      if (errorCallback) errorCallback(error);
    });
}

export const applyTimeZone = (date, tz='Spain') => {
  if (COLLA === 'eire') {
      // Irish time - handles both GMT (winter) and IST (summer)
      const mom = moment(date)
      const lastSundayOfThisYearsMarch = mom.clone().year(mom.clone().year()).month(2).endOf('month').day('Sunday');
      const lastSundayOfThisYearsOctober = mom.clone().year(mom.clone().year()).month(9).endOf('month').day('Sunday');

      if (mom < lastSundayOfThisYearsMarch) {
          // January - March: Winter time (GMT, UTC+0)
          return mom.add(0, 'hours');
      } else if (mom < lastSundayOfThisYearsOctober) {
          // April - October: Summer time (IST, UTC+1)
          return mom.add(-1, 'hours');
      } else {
          // November - December: Winter time (GMT, UTC+0)
          return mom.add(0, 'hours');
      }
  }

  if (tz === 'Spain') {
      const mom = moment(date)
      const lastSundayOfThisYearsMarch = mom.clone().year(mom.clone().year()).month(2).endOf('month').day('Sunday');
      const lastSundayOfThisYearsOctober = mom.clone().year(mom.clone().year()).month(9).endOf('month').day('Sunday');

      if (mom < lastSundayOfThisYearsMarch) {
          // January - March: Winter time
          return mom.add(-1, 'hours');
      } else if (mom < lastSundayOfThisYearsOctober) {
          // April - October: Summer time
          return mom.add(-2, 'hours');
      } else {
          // November - December: Winter time
          return mom.add(-1, 'hours');
      }
  } else {
      return moment(date);
  }
}

export const applyInverseTimeZone = (date=null, tz='Spain') => {
  if (COLLA === 'eire') {
      // Irish time - handles both GMT (winter) and IST (summer)
      const mom = date ? moment(date) : moment()
      const lastSundayOfThisYearsMarch = mom.clone().year(mom.clone().year()).month(2).endOf('month').day('Sunday');
      const lastSundayOfThisYearsOctober = mom.clone().year(mom.clone().year()).month(9).endOf('month').day('Sunday');

      if (mom < lastSundayOfThisYearsMarch) {
          // January - March: Winter time (GMT, UTC+0)
          return mom.add(0, 'hours');
      } else if (mom < lastSundayOfThisYearsOctober) {
          // April - October: Summer time (IST, UTC+1)
          return mom.add(1, 'hours');
      } else {
          // November - December: Winter time (GMT, UTC+0)
          return mom.add(0, 'hours');
      }
  }

  if (tz === 'Spain') {
      const mom = date ? moment(date) : moment()
      const lastSundayOfThisYearsMarch = mom.clone().year(mom.clone().year()).month(2).endOf('month').day('Sunday');
      const lastSundayOfThisYearsOctober = mom.clone().year(mom.clone().year()).month(9).endOf('month').day('Sunday');

      if (mom < lastSundayOfThisYearsMarch) {
          // January - March: Winter time
          return mom.add(1, 'hours');
      } else if (mom < lastSundayOfThisYearsOctober) {
          // April - October: Summer time
          return mom.add(2, 'hours');
      } else {
          // November - December: Winter time
          return mom.add(1, 'hours');
      }
  } else {
      return date ? moment(date) : moment()
  }
}

/**
 * Parse date string as local time (ignoring UTC conversion)
 * Use this when the database stores local time with a Z suffix
 * that should be interpreted as local, not UTC
 */
export const parseAsLocalTime = (dateString) => {
    if (!dateString) return new Date();
    // Remove timezone suffix (Z or +00:00) to treat as local time
    const localString = dateString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
    // Parse as local time by using the date parts directly
    const parts = localString.match(/(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?/);
    if (parts) {
        return new Date(
            parseInt(parts[1]), // year
            parseInt(parts[2]) - 1, // month (0-indexed)
            parseInt(parts[3]), // day
            parseInt(parts[4] || 0), // hours
            parseInt(parts[5] || 0) // minutes
        );
    }
    return new Date(dateString);
};

export const getFromCache = async (key) => {
    try {
        const item = localStorage.getItem(key);
        if (!item) return null;
        const parsed = JSON.parse(item);

        // New cache format with metadata: { savedAt, data }
        if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'savedAt')) {
            const savedAtMs = new Date(parsed.savedAt).getTime();
            const hasValidDate = !Number.isNaN(savedAtMs);
            const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
            if (!hasValidDate || (Date.now() - savedAtMs) > sevenDaysMs) {
                localStorage.removeItem(key);
                return null;
            }
            return parsed.data ?? null;
        }

        // Legacy cache entries without metadata are treated as expired.
        localStorage.removeItem(key);
        return null;
    } catch (e) {
        console.error('Error reading from cache:', e);
        return null;
    }
};

export const save2Cache = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify({
            savedAt: new Date().toISOString(),
            data: value,
        }));
    } catch (e) {
        console.error('Error saving to cache:', e);
    }
};

/**
 * Determines if an event is a musicians' rehearsal.
 * Explicit categories take precedence. Legacy payloads fall back to:
 * - It has target "músics", OR
 * - Its title contains "gralles", "tabals", or "músics" (case-insensitive)
 */
export const isAssaigDeMusics = (event) => {
    if (!event) return false;
    
    if (event.event_category) return event.event_category === 'musics';

    // Compatibility with backends that have not deployed event categories yet.
    if (event.targets?.includes('músics')) return true;
    
    // Check title for musician-related keywords
    const title = (event.title || '').toLowerCase();
    const musicKeywords = ['gralles', 'tabals', 'músics'];
    return musicKeywords.some(keyword => title.includes(keyword));
};

/**
 * Determines if an event is a meeting (reunió).
 * An event is considered a meeting if its title contains:
 * "reunió", "tècnica", or "junta" (case-insensitive)
 */
export const isReunio = (event) => {
    if (!event) return false;
    
    const title = (event.title || '').toLowerCase();
    const meetingKeywords = ['reunió', 'tècnica', 'junta'];
    return meetingKeywords.some(keyword => title.includes(keyword));
};
